<template>
  <div class="relative flex w-full items-center rounded-md border border-strong bg-surface" data-testid="speaker-search">
    <SvgIcon :path="mdiMagnify" :size="25" class="ml-3 text-main" />
    <input
      v-model="speakerSearch"
      class="w-full bg-surface px-1 py-2 focus:outline-none"
      type="text"
      data-testid="speaker-search-input"
      :placeholder="t('Speakers.search.title')"
      @input="searchSpeakers"
    />
    <SvgIcon
      v-if="speakerSearch.length > 2"
      :path="mdiClose"
      class="cursor-pointer pr-2 text-main"
      :size="22"
      data-testid="speaker-search-clear"
      @click="speakerSearch = ''"
    />
    <div v-if="speakerSearch.length > 2" class="absolute top-11 w-full bg-surface shadow-lg" data-testid="speaker-search-results">
      <div v-if="speakers.length" class="h-auto">
        <RouterLink
          v-for="speaker in speakers"
          :key="speaker.id"
          :to="{ name: 'viewSpeaker', params: { speakerId: toTypeAndId(speaker.id)['id'] || '' } }"
          class="flex cursor-pointer px-4 py-2 align-middle hover:bg-surface-elevation-4"
          :data-testid="`speaker-search-result-${toTypeAndId(speaker.id)['id'] || ''}`"
        >
          <img width="40" height="40" :src="speaker.photoUrl || ''" />
          <div class="ml-4">
            <p class="text-body-s">{{ speaker.name }}</p>
            <p class="text-label-s text-main">
              <span v-if="speaker.title">{{ speaker.title }} · </span>{{ speaker.partnerCompanyName }}
            </p>
          </div>
        </RouterLink>
      </div>
      <div v-if="speakers.length === 0 && !loading" class="my-4 ml-4 text-subtle" data-testid="speaker-search-no-results">
        <p>{{ t('Speakers.search.noSpeakersFound') }}</p>
      </div>
      <template v-if="loading">
        <ProgressBar class="mb-4" data-testid="speaker-search-loading" />
        <div class="mb-4 ml-4 text-subtle">
          <p>{{ t('Speakers.search.loading') }}</p>
        </div>
      </template>
      <RouterLink
        :to="{ name: 'createSpeaker' }"
        class="flex cursor-pointer items-center border-t border-t-main px-4 py-4 text-body-s hover:bg-surface-elevation-4"
        data-testid="speaker-search-create-link"
      >
        <SvgIcon :path="mdiPlusCircleOutline" :size="22" />
        <p class="ml-1">
          {{ t('Speakers.search.createNewProfile') }} "<span class="txt-body-s-semibold">{{ speakerSearch }}</span
          >"
        </p>
      </RouterLink>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { mdiMagnify, mdiPlusCircleOutline, mdiClose } from '@mdi/js'
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'

import { useSpeakersLazyQuery } from '@/gql/myomr'
import { toTypeAndId } from '@/helpers/globalIdHelper'

const { t } = useI18n()

const SEARCH_RESULT_LIMIT = 10

const { load, refetch, result, loading } = useSpeakersLazyQuery({}, { errorPolicy: 'all' })

const speakers = computed(() => result.value?.speakers.nodes || [])

const speakerSearch = ref('')

let debounce: ReturnType<typeof setTimeout>

watch(speakerSearch, async (newValue) => {
  if (newValue.length >= 3) {
    const vars = { limit: SEARCH_RESULT_LIMIT, offset: 0, searchTerm: newValue, forCurrentPartnerCompany: false }

    if (!load(null, vars)) {
      refetch(vars)
    }
  }
})

function searchSpeakers(input: Event) {
  clearTimeout(debounce)
  debounce = setTimeout(() => {
    speakerSearch.value = (input.target as HTMLInputElement).value
  }, 600)
}
</script>
